import React, { FC, useCallback, useMemo, useState, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import {
    Alert,
    Autocomplete,
    Box,
    Checkbox,
    IconButton,
    Paper,
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableRow,
    TextField,
    Tooltip,
} from '@mui/material';
import TimeInput from 'components/shared/time-input';
import DeleteIcon from '@mui/icons-material/Delete';
import DragIndicatorIcon from '@mui/icons-material/DragIndicator';
import { DragDropContext, Droppable, Draggable, DropResult } from '@hello-pangea/dnd';
import InfoText from 'components/shared/info-text.component';
import { useWorkflowSteps } from 'context/workflow-steps-context';
import { useDepartments } from 'context/departments-context';
import AddStepAutocomplete, {
    StepOption,
} from 'components/shared/add-step-autocomplete/add-step-autocomplete.component';
import { useQuery } from '@apollo/client';
import { WorkFlow } from 'models/workflow-step';
import { ProductionPlanningTemplate, Machine } from 'models/organization';
import { machinesTemplate } from 'apollo/templates/organization/query.graphql';

type PlanningStep = {
    _id: string;
    name: string;
    departmentId: string;
    machineId?: string;
    estimatedProductionHours?: number;
    estimatedMachineHours?: number;
    allowOvernight?: boolean;
    startNextDay?: boolean;
    affectedByQuantityModifier?: number;
};

type ProductionPlanTemplateStepsProps = {
    productionTemplate: ProductionPlanningTemplate | null;
    onStepsChange?: (
        steps: PlanningStep[],
        isValid?: boolean,
        hasStepsWithoutDept?: boolean,
        hasInvalidTimeInputs?: boolean,
    ) => void;
};
const ProductionPlanTemplateSteps: FC<ProductionPlanTemplateStepsProps> = ({ productionTemplate, onStepsChange }) => {
    const { t: translate } = useTranslation(['lab-settings', 'workflow']);
    const { workFlow } = useWorkflowSteps();
    const { departments, getDepartmentNameForStep } = useDepartments();
    const { data: machinesData } = useQuery<{ machines: Machine[] }>(machinesTemplate);
    const machines = machinesData?.machines ?? [];
    const workflowSteps = (workFlow as WorkFlow)?.steps || [];

    const getInitialSteps = (template: ProductionPlanningTemplate | null): PlanningStep[] => {
        return (template?.workflowSteps || []).map((step) => ({
            ...(step as any),
            _id: (step as any).stepId || (step as any)._id || '',
            estimatedProductionHours: (step as any).estimatedProductionHours ?? 0,
            estimatedMachineHours: (step as any).estimatedMachineHours ?? 0,
            allowOvernight: (step as any).allowOvernight ?? false,
            startNextDay: (step as any).startNextDay ?? false,
            affectedByQuantityModifier: (step as any).affectedByQuantityModifier ?? 0,
            departmentId: (step as any).departmentId || '',
            machineId: (step as any).machineId || undefined,
        }));
    };

    const [selectedSteps, setSelectedSteps] = useState<PlanningStep[]>(() => getInitialSteps(productionTemplate));

    const invalidTimeInputsRef = useRef<Set<string>>(new Set());

    useEffect(() => {
        setSelectedSteps(getInitialSteps(productionTemplate));
        invalidTimeInputsRef.current = new Set();
    }, [productionTemplate]);

    const checkStepsWithoutDept = useCallback(
        (steps: PlanningStep[]) =>
            steps.some(
                (step) => !departments.some((dept) => dept.workflowSteps.some((deptStep) => deptStep._id === step._id)),
            ),
        [departments],
    );

    const notify = useCallback(
        (steps: PlanningStep[]) => {
            onStepsChange?.(steps, true, checkStepsWithoutDept(steps), invalidTimeInputsRef.current.size > 0);
        },
        [onStepsChange, checkStepsWithoutDept],
    );

    const handleDeleteStep = useCallback(
        (stepId: string) => {
            const updated = selectedSteps.filter((step) => step._id !== stepId);
            setSelectedSteps(updated);
            notify(updated);
        },
        [selectedSteps, notify],
    );

    const handleAddStep = useCallback(
        (selected: StepOption) => {
            if (selectedSteps.some((step) => step._id === selected.id)) return;
            const workflowStep = workflowSteps.find((step) => step._id === selected.id);
            if (!workflowStep) return;
            const newStep: PlanningStep = {
                _id: workflowStep._id || '',
                name: workflowStep.name,
                departmentId: workflowStep._id || '',
                estimatedProductionHours: 0,
                estimatedMachineHours: 0,
                allowOvernight: false,
                startNextDay: false,
                affectedByQuantityModifier: 0,
            };
            const updated = [...selectedSteps, newStep];
            setSelectedSteps(updated);
            notify(updated);
        },
        [selectedSteps, workflowSteps, notify],
    );

    const handleDragEnd = useCallback(
        (result: DropResult) => {
            if (!result.destination) return;
            const reordered = [...selectedSteps];
            const [removed] = reordered.splice(result.source.index, 1);
            reordered.splice(result.destination.index, 0, removed);
            setSelectedSteps(reordered);
            notify(reordered);
        },
        [selectedSteps, notify],
    );

    const handleEstimatedHoursChange = useCallback(
        (index: number, value: number | undefined) => {
            const updated = [...selectedSteps];
            updated[index] = { ...updated[index], estimatedProductionHours: value };
            setSelectedSteps(updated);
            notify(updated);
        },
        [selectedSteps, notify],
    );

    const handleEstimatedMachineHoursChange = useCallback(
        (index: number, value: number | undefined) => {
            const updated = [...selectedSteps];
            updated[index] = { ...updated[index], estimatedMachineHours: value };
            setSelectedSteps(updated);
            notify(updated);
        },
        [selectedSteps, notify],
    );

    const handleTimeInputValidChange = useCallback(
        (index: number, field: 'production' | 'idle', isValid: boolean) => {
            const key = `${index}-${field}`;
            const next = new Set(invalidTimeInputsRef.current);
            if (isValid) {
                next.delete(key);
            } else {
                next.add(key);
            }
            invalidTimeInputsRef.current = next;
            onStepsChange?.(selectedSteps, true, checkStepsWithoutDept(selectedSteps), next.size > 0);
        },
        [selectedSteps, onStepsChange, checkStepsWithoutDept],
    );

    const handleBooleanChange = useCallback(
        (index: number, field: 'allowOvernight' | 'startNextDay', value: boolean) => {
            const updated = [...selectedSteps];
            updated[index] = { ...updated[index], [field]: value };
            setSelectedSteps(updated);
            notify(updated);
        },
        [selectedSteps, notify],
    );

    const stepsWithoutDepartment = useMemo(
        () =>
            selectedSteps.filter(
                (step) => !departments.some((dept) => dept.workflowSteps.some((deptStep) => deptStep._id === step._id)),
            ),
        [selectedSteps, departments],
    );

    const stepOptions: StepOption[] = useMemo(
        () =>
            workflowSteps
                .filter((step) => !selectedSteps.some((selected) => selected._id === step._id))
                .map((step) => ({
                    id: step._id || '',
                    name: step.name,
                    departmentName: getDepartmentNameForStep(step._id || ''),
                })),
        [workflowSteps, selectedSteps, getDepartmentNameForStep],
    );

    return (
        <Box>
            <Paper variant="outlined">
                <DragDropContext onDragEnd={handleDragEnd}>
                    <Droppable droppableId="workflow-steps-droppable">
                        {(provided) => (
                            <Table size="small" ref={provided.innerRef} {...provided.droppableProps}>
                                <TableHead>
                                    <TableRow>
                                        <TableCell colSpan={2}>{translate('lab-settings:WORKFLOW_STEPS')}</TableCell>
                                        <TableCell sx={{ width: 130 }}>
                                            <Tooltip title={translate('lab-settings:PRODUCTION_HOURS_TOOLTIP')}>
                                                <span>{translate('lab-settings:PRODUCTION_HOURS')}</span>
                                            </Tooltip>
                                        </TableCell>
                                        <TableCell sx={{ width: 120 }} align="center">
                                            <Tooltip title={translate('lab-settings:AFFECTED_BY_QUANTITY_TOOLTIP')}>
                                                <span>{translate('lab-settings:AFFECTED_BY_QUANTITY_ABBR')}</span>
                                            </Tooltip>
                                        </TableCell>
                                        <TableCell sx={{ width: 300 }}>
                                            <Tooltip title={translate('lab-settings:MACHINE_TOOLTIP')}>
                                                <span>{translate('lab-settings:MACHINE')}</span>
                                            </Tooltip>
                                        </TableCell>
                                        <TableCell sx={{ width: 130 }}>
                                            <Tooltip title={translate('lab-settings:MACHINE_HOURS_TOOLTIP')}>
                                                <span>{translate('lab-settings:MACHINE_HOURS')}</span>
                                            </Tooltip>
                                        </TableCell>
                                        <TableCell sx={{ width: 100 }} align="center">
                                            <Tooltip title={translate('lab-settings:ALLOW_OVERNIGHT_TOOLTIP')}>
                                                <span>{translate('lab-settings:ALLOW_OVERNIGHT_ABBR')}</span>
                                            </Tooltip>
                                        </TableCell>
                                        <TableCell sx={{ width: 100 }} align="center">
                                            <Tooltip title={translate('lab-settings:START_NEXT_DAY_TOOLTIP')}>
                                                <span>{translate('lab-settings:START_NEXT_DAY_ABBR')}</span>
                                            </Tooltip>
                                        </TableCell>
                                        <TableCell sx={{ width: 20 }} />
                                    </TableRow>
                                </TableHead>

                                <TableBody>
                                    {selectedSteps.map((step, index) => (
                                        <Draggable key={step._id} draggableId={step._id} index={index}>
                                            {(provider, snapshot) => (
                                                <TableRow
                                                    ref={provider.innerRef}
                                                    {...provider.draggableProps}
                                                    style={{
                                                        ...provider.draggableProps.style,
                                                        display: snapshot.isDragging ? 'table' : 'table-row',
                                                        background: snapshot.isDragging ? '#f3f3f3' : undefined,
                                                    }}
                                                >
                                                    <TableCell
                                                        {...provider.dragHandleProps}
                                                        sx={{ width: 0, minWidth: 0 }}
                                                    >
                                                        <DragIndicatorIcon />
                                                    </TableCell>

                                                    <TableCell sx={{ padding: '10px' }}>
                                                        {getDepartmentNameForStep(step._id)} - {step.name}
                                                    </TableCell>

                                                    <TableCell>
                                                        <TimeInput
                                                            size="small"
                                                            value={step.estimatedProductionHours}
                                                            onChange={(value) =>
                                                                handleEstimatedHoursChange(index, value)
                                                            }
                                                            onValidChange={(isValid) =>
                                                                handleTimeInputValidChange(index, 'production', isValid)
                                                            }
                                                        />
                                                    </TableCell>

                                                    <TableCell align="center">
                                                        <TextField
                                                            type="number"
                                                            size="small"
                                                            value={step.affectedByQuantityModifier ?? 0}
                                                            onChange={(e) => {
                                                                const updated = [...selectedSteps];
                                                                updated[index] = {
                                                                    ...updated[index],
                                                                    affectedByQuantityModifier:
                                                                        parseFloat(e.target.value) || 0,
                                                                };
                                                                setSelectedSteps(updated);
                                                                notify(updated);
                                                            }}
                                                            inputProps={{ min: 0, step: 0.1 }}
                                                            sx={{ width: 80 }}
                                                        />
                                                    </TableCell>

                                                    <TableCell>
                                                        <Autocomplete
                                                            size="small"
                                                            options={machines}
                                                            getOptionLabel={(option) =>
                                                                option.machineSchedule?.length
                                                                    ? `${option.name} (${option.machineSchedule.join(', ')})`
                                                                    : option.name
                                                            }
                                                            value={
                                                                machines.find((m) => m._id === step.machineId) ?? null
                                                            }
                                                            onChange={(_e, selected) => {
                                                                const updated = [...selectedSteps];
                                                                updated[index] = {
                                                                    ...updated[index],
                                                                    machineId: selected?._id || undefined,
                                                                    estimatedMachineHours: selected
                                                                        ? undefined
                                                                        : updated[index].estimatedMachineHours,
                                                                };
                                                                setSelectedSteps(updated);
                                                                notify(updated);
                                                            }}
                                                            renderInput={(params) => <TextField {...params} />}
                                                            isOptionEqualToValue={(option, value) =>
                                                                option._id === value._id
                                                            }
                                                            slotProps={{
                                                                popper: { style: { width: 'auto', minWidth: 350 } },
                                                            }}
                                                        />
                                                    </TableCell>

                                                    <TableCell>
                                                        <TimeInput
                                                            size="small"
                                                            disabled={!!step.machineId}
                                                            value={
                                                                step.machineId
                                                                    ? machines.find((m) => m._id === step.machineId)
                                                                          ?.defaultMachineTime ?? 0
                                                                    : step.estimatedMachineHours ?? 0
                                                            }
                                                            onChange={(value) =>
                                                                handleEstimatedMachineHoursChange(index, value)
                                                            }
                                                            onValidChange={(isValid) =>
                                                                handleTimeInputValidChange(index, 'idle', isValid)
                                                            }
                                                        />
                                                    </TableCell>

                                                    <TableCell align="center">
                                                        <Checkbox
                                                            checked={step.allowOvernight || false}
                                                            onChange={(e) =>
                                                                handleBooleanChange(
                                                                    index,
                                                                    'allowOvernight',
                                                                    e.target.checked,
                                                                )
                                                            }
                                                        />
                                                    </TableCell>

                                                    <TableCell align="center">
                                                        <Checkbox
                                                            checked={step.startNextDay || false}
                                                            onChange={(e) =>
                                                                handleBooleanChange(
                                                                    index,
                                                                    'startNextDay',
                                                                    e.target.checked,
                                                                )
                                                            }
                                                        />
                                                    </TableCell>

                                                    <TableCell align="right">
                                                        <IconButton onClick={() => handleDeleteStep(step._id)}>
                                                            <DeleteIcon />
                                                        </IconButton>
                                                    </TableCell>
                                                </TableRow>
                                            )}
                                        </Draggable>
                                    ))}

                                    {provided.placeholder}

                                    {!selectedSteps.length && (
                                        <TableRow>
                                            <TableCell colSpan={9}>
                                                <InfoText text={translate('lab-settings:NO_STEPS_ADDED')} />
                                            </TableCell>
                                        </TableRow>
                                    )}
                                </TableBody>
                            </Table>
                        )}
                    </Droppable>
                </DragDropContext>
            </Paper>

            {stepsWithoutDepartment.length > 0 && (
                <Box mt={1} textAlign="right">
                    <Alert severity="error">{translate('lab-settings:STEPS_WITHOUT_DEPARTMENT_ERROR')}</Alert>
                </Box>
            )}

            <Box mt={2}>
                {stepOptions.length ? (
                    <AddStepAutocomplete options={stepOptions} onStepSelected={handleAddStep} />
                ) : (
                    <InfoText text={translate('lab-settings:ALL_WORKFLOW_STEPS_ADDED')} />
                )}
            </Box>
        </Box>
    );
};

export default ProductionPlanTemplateSteps;
